import type { AiChatTap } from '@euler/app/flows/ai-suite/conversation';
import { useCurrentUserId } from '@euler/app/flows/auth/context';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import type { SSEConnection } from '@euler/lib/request/SSEClient';
import {
  AIAssistantMessage,
  AIPageContext,
  AISuggestion,
  AiClientFeature,
  AiSessionType,
} from '@euler/model/ai';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { BiAnswer } from '@euler/model/bi/answer';
import { useServiceFactory } from '@euler/services/factory';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
  useWatchValue,
} from '@euler/utils/hooks';
import produce, { Draft } from 'immer';
import { nanoid } from 'nanoid/non-secure';
import React, {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/** The overlay renders the BI widgets (AIContentRenderer). */
const kClientFeatures = [AiClientFeature.Bi];

// ─── State ──────────────────────────────────────────

export type AIAssistantState = {
  messages: AIAssistantMessage[];
  isStreaming: boolean;
  isLoading: boolean;
  isSuggestionsLoading: boolean;
  sessionId?: string;
  suggestions: AISuggestion[];
  error?: string;
  currentToolCall?: string;
};

const initialState: AIAssistantState = {
  messages: [],
  isStreaming: false,
  isLoading: false,
  isSuggestionsLoading: false,
  suggestions: [],
};

// ─── Context ────────────────────────────────────────

export type AIAssistantContextValue = {
  visible$: BehaviorSubject<boolean>;
  state$: BehaviorSubject<AIAssistantState>;
  /**
   * Active page context. When non-null, some focused screen has opted
   * into the AI assistant via `useAIAssistantForPage`; the FAB shows
   * and tools see this context. When null, no screen is AI-enabled and
   * the FAB is hidden.
   */
  pageContext$: BehaviorSubject<AIPageContext | undefined>;
  /**
   * Refcount of transient overlays asking the FAB to hide (e.g. a
   * bottom-sheet whose footer would overlap the FAB). Orthogonal to
   * `pageContext$` — the page still wants the FAB, the overlay just
   * covers the space temporarily. Pages that don't want the FAB at
   * all should simply not call `useAIAssistantForPage`.
   */
  buttonSuppressCount$: BehaviorSubject<number>;
  /** Hide the FAB while a transient overlay is up; cleanup restores it. */
  suppressButton: () => () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /**
   * Sends a turn; false when nothing was sent (empty, or a reply is still
   * running). A BI widget's tap rides along as `biAnswer`, a file it took
   * as `attachments`.
   */
  sendMessage: (text: string, extra?: AIAssistantSendExtra) => boolean;
  /** Sends the last turn again, its tap and files included. */
  retry: () => boolean;
  /** Each tap whose reply ended or was stopped, for the card that sent it. */
  tapSettled$: Observable<AiChatTap>;
  /** Asks the composer to take the cursor ("adjust it in words"). */
  composerFocus$: Subject<void>;
  clearSession: () => Promise<void>;
  stopStreaming: () => void;
  toggleReaction: (
    messageId: string,
    localId: string,
    reaction: 'up' | 'down',
  ) => void;
};

export interface AIAssistantSendExtra {
  biAnswer?: BiAnswer;
  attachments?: AiAttachmentInfo[];
}

const AIAssistantContext = createContext<AIAssistantContextValue>(null as any);

export function useAIAssistant(): AIAssistantContextValue {
  return useContext(AIAssistantContext);
}

export function useAIAssistantVisible(): boolean {
  const { visible$ } = useAIAssistant();
  return useBehaviorSubject(visible$)[0];
}

export function useAIAssistantState(): AIAssistantState {
  const { state$ } = useAIAssistant();
  return useBehaviorSubject(state$)[0];
}

/**
 * True when a focused screen has opted into the AI assistant via
 * `useAIAssistantForPage` AND no transient overlay is suppressing the
 * FAB. Pages that don't call the hook get no FAB at all.
 */
export function useAIAssistantButtonVisible(): boolean {
  const { pageContext$, buttonSuppressCount$ } = useAIAssistant();
  const pageContext = useBehaviorSubject(pageContext$)[0];
  const suppressCount = useBehaviorSubject(buttonSuppressCount$)[0];
  return pageContext != null && suppressCount === 0;
}

export function useAIAssistantActions() {
  const ctx = useAIAssistant();
  return ctx;
}

// ─── Tool name mapping ──────────────────────────────

/**
 * What the assistant says it is consulting, in the technician's words. Name
 * the SUBJECT, never the mechanism: 「按车牌查工单」/「按品牌查车辆」 named
 * which index the lookup went through, which is our business, not theirs —
 * both are just the order or the vehicle. Several tools sharing one label is
 * fine and expected.
 *
 * The subject is the one the backend's own tool description names
 * (`packages/ai/src/tools/*.ts`, catalogued in the workshop-assistant
 * prompt), in the word euler's UI already uses for it — 遗留问题 not
 * 待处理问题, 员工 not 成员.
 */
const toolLabels: Record<string, string> = {
  search_order_refs_by_lpn: '查询车辆工单',
  search_vehicles_by_lpn_fragment: '查询车辆信息',
  search_vehicles_by_brand: '查询车辆信息',
  get_current_active_order_ref_by_lpn: '查询在店工单',
  get_latest_order_ref_by_lpn: '查询工单信息',
  get_task: '查询工单概要',
  get_task_service_items: '查询服务项目',
  get_task_findings: '查询检测结果',
  get_task_inspection_scope: '查询检测项目',
  get_task_construction_info: '查询施工信息',
  get_task_delivery_check_info: '查询交车检',
  get_task_quotation: '查询报价单',
  get_task_settlement: '查询结算单',
  search_service_records: '查询历史服务记录',
  get_service_record: '查询服务记录详情',
  get_chat_history: '查询聊天记录',
  get_vehicle_maintenance_plan_items: '查询保养计划',
  get_shop_performance_overview: '查询门店运营概况',
  get_shop_statistics: '查询统计趋势',
  get_shop_maintenance_alerts: '查询保养预警',
  get_shop_pending_issues: '查询遗留问题',
  get_my_workload: '查询我的工作',
  get_member_info: '查询员工信息',
  get_member_service_history: '查询员工经手车辆',
  get_staff_performance: '查询员工表现',
  get_service_efficiency: '查询服务效率',
  get_conversion_stats: '查询转化率',
  get_followup_opportunities: '查询待跟进客户',
  get_customer_engagement: '查询客户互动',
  get_service_item_stats: '查询施工项目统计',
  get_feed_digest: '汇总通知',
  search_feed: '搜索通知',
  get_feed_statistics: '分析通知构成',
  check_feed_follow_up: '核对通知跟进情况',
  get_chat_backlog: '查询待回复消息',
  get_chat_digest: '汇总聊天记录',
  search_chat: '搜索聊天记录',
  get_leads_overview: '查询销售线索总览',
  search_leads: '搜索销售线索',
  get_reservations_overview: '查询预约总览',
  search_reservations: '搜索预约',
};

// ─── Helpers ────────────────────────────────────────

function findMsg(draft: Draft<AIAssistantState>, localId: string) {
  return draft.messages.find(m => m.localId === localId);
}

function completeStreaming(
  draft: Draft<AIAssistantState>,
  localId: string,
  messageId: string,
) {
  draft.isStreaming = false;
  draft.currentToolCall = undefined;
  const msg = findMsg(draft, localId);
  if (msg) {
    msg.id = messageId;
    msg.isStreaming = false;
  }
}

function failStreaming(
  draft: Draft<AIAssistantState>,
  localId: string,
  error: string,
) {
  draft.isStreaming = false;
  draft.currentToolCall = undefined;
  draft.error = error;
  const msg = findMsg(draft, localId);
  if (msg) {
    msg.isStreaming = false;
    if (!msg.content) msg.content = '抱歉，出现了错误。请稍后再试。';
  }
}

// ─── Provider ───────────────────────────────────────

type Props = {
  sessionType?: string;
  children: ReactNode;
};

export const AIAssistantProvider = memo(function AIAssistantProvider({
  sessionType = AiSessionType.WorkshopAssistant,
  children,
}: Props) {
  const { aiAssistantService } = useServiceFactory();
  const role = useCurrentUserRole();
  const userId = useCurrentUserId();
  const connectionRef = useRef<SSEConnection | null>(null);
  /**
   * Which session's history is currently in `state.messages`. Threads are
   * per-subject server-side, so "loaded" is not a boolean: opening the
   * panel on a different car must replace the transcript, and opening it
   * again on the same one must not refetch.
   */
  const loadedSessionIdRef = useRef<string | null>(null);

  const visible$ = useCreation(() => new BehaviorSubject(false), []);
  const state$ = useCreation(
    () => new BehaviorSubject<AIAssistantState>(initialState),
    [],
  );
  const pageContext$ = useCreation(
    () => new BehaviorSubject<AIPageContext | undefined>(undefined),
    [],
  );
  const buttonSuppressCount$ = useCreation(() => new BehaviorSubject(0), []);
  const tapSettled$ = useCreation(() => new Subject<AiChatTap>(), []);
  const composerFocus$ = useCreation(() => new Subject<void>(), []);
  /** The tap the running reply answers, settled when that reply ends. */
  const pendingTapRef = useRef<AiChatTap | null>(null);
  const settleTap = usePersistFn(() => {
    const tap = pendingTapRef.current;
    pendingTapRef.current = null;
    if (tap) tapSettled$.next(tap);
  });
  /** What the last turn sent, for retrying it as it was. */
  const lastTurnRef = useRef<{
    text: string;
    extra?: AIAssistantSendExtra;
  } | null>(null);
  /**
   * Bumped whenever a turn stops being the current one (stopped, replaced,
   * another user), so its late callbacks and a pending rate-limit retry
   * leave the newer turn alone.
   */
  const generationRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelTurn = usePersistFn(() => {
    generationRef.current += 1;
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    connectionRef.current?.abort();
    connectionRef.current = null;
  });

  const updateState = usePersistFn(
    (recipe: (draft: Draft<AIAssistantState>) => void) => {
      state$.next(produce(state$.getValue(), recipe));
    },
  );

  useWatchValue(userId, prev => {
    if (prev === undefined) return;
    cancelTurn();
    pendingTapRef.current = null;
    lastTurnRef.current = null;
    loadedSessionIdRef.current = null;
    visible$.next(false);
    state$.next(initialState);
    pageContext$.next(undefined);
    buttonSuppressCount$.next(0);
  });

  const suppressButton = useCallback(() => {
    buttonSuppressCount$.next(buttonSuppressCount$.getValue() + 1);
    return () => {
      buttonSuppressCount$.next(
        Math.max(0, buttonSuppressCount$.getValue() - 1),
      );
    };
  }, [buttonSuppressCount$]);

  const open = useCallback(() => {
    visible$.next(true);
  }, [visible$]);

  const close = useCallback(() => {
    visible$.next(false);
  }, [visible$]);

  const toggle = useCallback(() => {
    visible$.next(!visible$.getValue());
  }, [visible$]);

  /**
   * Resolve the thread for the screen the panel was opened on, and load
   * its transcript if it is not the one already shown.
   *
   * Resolution happens on every open rather than once per app run: the
   * server keys a thread on what the conversation is ABOUT, so the same
   * user opening the panel on a different car gets a different thread.
   */
  const loadHistory = usePersistFn(async () => {
    updateState(draft => {
      draft.isLoading = true;
    });
    try {
      const session = await aiAssistantService.getSessionForPage(
        sessionType,
        pageContext$.getValue(),
      );
      if (session == null) {
        updateState(draft => {
          draft.isLoading = false;
        });
        return;
      }
      if (loadedSessionIdRef.current === session.id) {
        updateState(draft => {
          draft.sessionId = session.id;
          draft.isLoading = false;
        });
        return;
      }

      const result = await aiAssistantService.getMessages(session.id, {
        limit: 50,
      });
      const msgs: AIAssistantMessage[] = (result.messages ?? [])
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          id: m.id,
          localId: nanoid(),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          createdAt: new Date(m.createdAt),
        }));
      loadedSessionIdRef.current = session.id;
      updateState(draft => {
        draft.sessionId = session.id;
        draft.messages = msgs;
        draft.isLoading = false;
      });
    } catch (e) {
      console.error('[ai] failed to load history:', e);
      updateState(draft => {
        draft.isLoading = false;
      });
    }
  });

  useEffect(() => {
    const sub = visible$.subscribe(visible => {
      if (visible) {
        void loadHistory();
      }
    });
    return () => sub.unsubscribe();
  }, [visible$, loadHistory]);

  const loadSuggestions = usePersistFn(async () => {
    const pageCtx = pageContext$.getValue();
    if (!pageCtx) return;
    updateState(draft => {
      draft.isSuggestionsLoading = true;
    });
    try {
      const suggestions = await aiAssistantService.getSuggestions(
        sessionType,
        pageCtx,
        role,
      );
      updateState(draft => {
        draft.suggestions = suggestions;
        draft.isSuggestionsLoading = false;
      });
    } catch (e) {
      console.error('[ai] failed to load suggestions:', e);
      updateState(draft => {
        draft.isSuggestionsLoading = false;
      });
    }
  });

  useEffect(() => {
    const sub = pageContext$.subscribe(() => {
      void loadSuggestions();
    });
    return () => sub.unsubscribe();
  }, [pageContext$, loadSuggestions]);

  const sendMessage = useCallback(
    (text: string, extra?: AIAssistantSendExtra): boolean => {
      const trimmed = text.trim();
      if (!trimmed) return false;

      const current = state$.getValue();
      // A history load would replace the reply this turn streams into.
      if (current.isStreaming || current.isLoading) return false;
      const biAnswer = extra?.biAnswer;
      // The server settles a tap within its conversation, so a tap needs
      // one (a card can outlive a first reply that failed before naming
      // its thread).
      if (biAnswer && !current.sessionId) return false;
      cancelTurn();
      const generation = generationRef.current;
      const live = () => generation === generationRef.current;
      lastTurnRef.current = { text: trimmed, extra };
      const attachments = (extra?.attachments ?? []).map(info => ({
        attachmentId: info.id,
      }));
      pendingTapRef.current = biAnswer
        ? { kind: 'bi', answer: biAnswer }
        : null;

      const userMsg: AIAssistantMessage = {
        localId: nanoid(),
        role: 'user',
        content: trimmed,
        createdAt: new Date(),
      };

      const assistantMsg: AIAssistantMessage = {
        localId: nanoid(),
        role: 'assistant',
        content: '',
        isStreaming: true,
        createdAt: new Date(),
      };

      updateState(draft => {
        draft.messages.push(userMsg, assistantMsg);
        draft.isStreaming = true;
        draft.error = undefined;
        draft.currentToolCall = undefined;
      });

      const assistantLocalId = assistantMsg.localId;
      const pageContext = pageContext$.getValue();
      // Name the conversation being continued. Without it the server falls
      // back to the single legacy session and the turn lands somewhere the
      // transcript on screen does not come from.
      const sessionId = state$.getValue().sessionId;

      let tokenBuffer = '';
      let flushTimer: ReturnType<typeof setTimeout> | null = null;
      const FLUSH_INTERVAL = 100;

      const flushTokens = () => {
        flushTimer = null;
        if (!tokenBuffer) return;
        const chunk = tokenBuffer;
        tokenBuffer = '';
        updateState(draft => {
          draft.currentToolCall = undefined;
          const msg = findMsg(draft, assistantLocalId);
          if (msg) msg.content += chunk;
        });
      };

      const request = {
        message: trimmed,
        sessionType,
        pageContext,
        sessionId,
        clientFeatures: kClientFeatures,
        ...(biAnswer ? { biAnswer } : {}),
        ...(attachments.length ? { attachments } : {}),
      };

      const makeCallbacks = (isRetry: boolean) => ({
        onSession: (sid: string) => {
          if (!live() || !sid) return;
          loadedSessionIdRef.current = sid;
          updateState(draft => {
            draft.sessionId = sid;
          });
        },
        onToken: (token: string) => {
          if (!live()) return;
          tokenBuffer += token;
          if (!flushTimer) {
            flushTimer = setTimeout(flushTokens, FLUSH_INTERVAL);
          }
        },
        // The label stays up until the answer resumes — `flushTokens`
        // clears it. Dropping it on `tool_result` showed the name only for
        // the call itself, a few hundred ms, while the long wait that
        // follows (the model reading the result) read as plain 正在思考.
        // A tool that names its own progress (the BI and APDM tools do)
        // is shown in its words; the local labels cover the older ones.
        onToolCall: (name: string, _args: unknown, progress?: string) => {
          if (!live()) return;
          if (!isRetry) flushTokens();
          const label = toolLabels[name];
          if (progress == null && label == null) {
            // A tool we have no Chinese label for: say something plain
            // rather than print the backend's function name at a
            // technician, and leave a trace so the gap gets closed.
            console.error(`[ai] unlabelled tool call: ${name}`);
          }
          updateState(draft => {
            // The labels are bare verb phrases; the shimmer reads them as
            // something in progress.
            draft.currentToolCall = progress ?? `正在${label ?? '查询'}`;
          });
        },
        onDone: (mid: string, sid: string) => {
          if (!live()) return;
          if (!isRetry) flushTokens();
          connectionRef.current = null;
          // The turn joined whichever thread the server picked. Record it
          // so the next open recognises the transcript on screen instead
          // of refetching it, or replaces it when it really did change.
          if (sid) loadedSessionIdRef.current = sid;
          updateState(draft => {
            if (sid) draft.sessionId = sid;
            completeStreaming(draft, assistantLocalId, mid);
          });
          settleTap();
        },
        onError: (msg: string, retryAfter?: number) => {
          if (!live()) return;
          connectionRef.current = null;
          if (!isRetry && retryAfter && retryAfter > 0) {
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              if (!live()) return;
              connectionRef.current = aiAssistantService.ask(
                request,
                makeCallbacks(true),
              );
            }, retryAfter);
            return;
          }
          updateState(draft => {
            failStreaming(draft, assistantLocalId, msg);
          });
          settleTap();
        },
      });

      connectionRef.current = aiAssistantService.ask(
        request,
        makeCallbacks(false),
      );
      return true;
    },
    [
      state$,
      updateState,
      pageContext$,
      aiAssistantService,
      sessionType,
      settleTap,
      cancelTurn,
    ],
  );

  const retry = useCallback((): boolean => {
    const last = lastTurnRef.current;
    return last ? sendMessage(last.text, last.extra) : false;
  }, [sendMessage]);

  const clearSession = useCallback(async () => {
    const s = state$.getValue();
    if (s.sessionId) {
      try {
        await aiAssistantService.clearSession(s.sessionId);
      } catch (e) {
        console.error('[ai] failed to clear session:', e);
      }
    }
    loadedSessionIdRef.current = null;
    updateState(draft => {
      draft.messages = [];
      draft.sessionId = undefined;
      draft.error = undefined;
    });
  }, [aiAssistantService, updateState, state$]);

  const stopStreaming = useCallback(() => {
    cancelTurn();
    updateState(draft => {
      draft.isStreaming = false;
      draft.currentToolCall = undefined;
      for (const m of draft.messages) {
        if (m.isStreaming) m.isStreaming = false;
      }
    });
    // The server settles a tap before any reply, so a stopped reply still
    // changed what the tap was for.
    settleTap();
  }, [updateState, settleTap, cancelTurn]);

  const toggleReaction = useCallback(
    (messageId: string, localId: string, reaction: 'up' | 'down') => {
      const msg = state$.getValue().messages.find(m => m.localId === localId);
      const newReaction = msg?.reaction === reaction ? null : reaction;

      updateState(draft => {
        const m = findMsg(draft, localId);
        if (m) m.reaction = newReaction;
      });

      if (messageId) {
        aiAssistantService
          .toggleReaction(messageId, newReaction ?? reaction)
          .catch(e => console.error('[ai] failed to toggle reaction:', e));
      }
    },
    [aiAssistantService, updateState, state$],
  );

  const contextValue = useMemo<AIAssistantContextValue>(
    () => ({
      visible$,
      state$,
      pageContext$,
      buttonSuppressCount$,
      suppressButton,
      open,
      close,
      toggle,
      sendMessage,
      retry,
      tapSettled$,
      composerFocus$,
      clearSession,
      stopStreaming,
      toggleReaction,
    }),
    [
      visible$,
      state$,
      pageContext$,
      buttonSuppressCount$,
      suppressButton,
      open,
      close,
      toggle,
      sendMessage,
      retry,
      tapSettled$,
      composerFocus$,
      clearSession,
      stopStreaming,
      toggleReaction,
    ],
  );

  useEffect(() => cancelTurn, [cancelTurn]);

  return (
    <AIAssistantContext.Provider value={contextValue}>
      {children}
    </AIAssistantContext.Provider>
  );
});
